import { LineChart, Table as TableIcon } from 'lucide-react';
import { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataVizCanvas } from './dataviz-canvas';
import { DataVizTableContent } from './dataviz-table';
import { useIsMobilePortrait } from './task-content';

import type { ResolvedDataVizData } from '@agavi/blocks';
import type { ReactNode } from 'react';

interface DataVizDisplayProps {
  data: ResolvedDataVizData;
  chartHeight?: number;
  /** The point currently sounding during sonification playback — forwarded straight through to
   * whichever DataVizCanvas is actually showing for the current display mode. */
  highlightedPoint?: { x: number | string; y: number } | null;
  /** The moving playhead x-position during sonification playback — same forwarding as
   * highlightedPoint, just independent of whether a point happens to be sounding right now. */
  playheadX?: number | null;
  /**
   * The series a teacher has armed for deletion, amber-tinted with its siblings dimmed.
   *
   * Forwarded for the same reason as the two above, and it arrived later than they did: while the
   * editor kept its own pinned chart, the arming feedback lived there. Once that duplicate went the
   * card became the only chart on screen, so the signal had to reach it or be lost.
   */
  armedSeriesId?: string | null;
  /** Rendered directly under the chart+legend, inside the same left-hand column as the graph (not
   * as a separate element below the whole display) — used by the collapsed block-display card,
   * which embeds the sonification player here so the data table (in 'always'/open-'slideout' mode)
   * can be stretched to match the full graph+legend+sonification column height, not just the
   * chart's own height. Omitted entirely by callers (like the isPreview render) that place their
   * own sonification player as a separate sibling below this component instead. */
  sonificationSlot?: ReactNode;
}

/**
 * The one place all three `dataTableDisplay` modes actually behave differently — DataVizCanvas and
 * DataVizTableContent are both mode-agnostic. 'always' and open-'slideout' share the same 2/3
 * graph : 1/3 table split (side-by-side on wide viewports, stacked top/bottom on mobile portrait —
 * matched to how the rest of this block already treats phone-portrait as its own layout, e.g. the
 * CSV import dialog's bottom sheet); 'toggle' is the one mode where the table takes over the whole
 * block width instead of sharing it with the graph.
 */
export function DataVizDisplay({ data, chartHeight = 260, highlightedPoint, playheadX, armedSeriesId, sonificationSlot }: DataVizDisplayProps) {
  const { t } = useTranslation();
  const isMobilePortrait = useIsMobilePortrait();
  const [toggleShowTable, setToggleShowTable] = useState(false);
  const [slideoutOpen, setSlideoutOpen] = useState(false);
  // Measures the graph(+legend+sonification) column's actual rendered height so the side-by-side
  // table (in 'always'/open-'slideout' mode) can be capped to match it exactly, scrolling internally
  // past that point — a bare flex row's `align-items: stretch` alone doesn't do this: if the
  // table's own content is taller than the graph column, stretch grows the WHOLE row (and the graph
  // column with it) to match the table instead, so a long table just keeps showing every row
  // uncapped. A callback ref (not a plain useRef) so the observer re-attaches whenever the mode
  // switch swaps in a different DOM node for "the left column".
  const [leftColumnEl, setLeftColumnEl] = useState<HTMLDivElement | null>(null);
  const [leftColumnHeight, setLeftColumnHeight] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    if (!leftColumnEl) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setLeftColumnHeight(entry.contentRect.height);
    });
    observer.observe(leftColumnEl);
    return () => observer.disconnect();
  }, [leftColumnEl]);
  const hasData = data.series.some((s) => s.points.length > 0);
  if (!hasData) return null;

  const mode = data.dataTableDisplay ?? 'toggle';
  const tableMaxHeight = isMobilePortrait ? chartHeight / 2 : chartHeight;
  // Falls back to chartHeight before the very first measurement resolves, so the table never briefly
  // flashes uncapped on first paint. This is an explicit `height` (not `max-height`) on the OUTER
  // frame — relying on the flex row's own `align-items: stretch` to size it doesn't reliably cap the
  // table (stretch just proposes a cross-size, it doesn't force content to respect it the way an
  // explicit height + child `overflow-y-auto` does). The inner div then uses `h-full`, which
  // resolves against the outer's CONTENT box automatically (border-box sizing), so it doesn't need
  // its own height arithmetic to account for the outer's padding.
  const sideBySideHeight = leftColumnHeight ?? chartHeight;

  if (mode === 'always') {
    return (
      <div className={isMobilePortrait ? 'flex flex-col gap-3' : 'flex gap-3'}>
        <div ref={isMobilePortrait ? undefined : setLeftColumnEl} className={isMobilePortrait ? 'flex flex-col gap-3' : 'flex w-2/3 min-w-0 flex-col gap-3'}>
          <DataVizCanvas data={data} height={chartHeight} highlightedPoint={highlightedPoint} playheadX={playheadX} armedSeriesId={armedSeriesId} />
          {sonificationSlot}
        </div>
        <div
          className={`rounded-lg border border-ws-border p-2 ${isMobilePortrait ? '' : 'w-1/3 min-w-0'}`}
          style={isMobilePortrait ? undefined : { height: sideBySideHeight }}
        >
          <div className='h-full overflow-y-auto' style={isMobilePortrait ? { maxHeight: tableMaxHeight } : undefined}>
            <DataVizTableContent data={data} />
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'toggle') {
    return (
      <div className='flex flex-col gap-1'>
        {/* A real switch (role='switch', not two buttons) in its own header row — NOT absolutely
            positioned over the chart, since DataVizCanvas centers an optional title across the
            full width and an overlay would sit right on top of it. Its label describes what the
            "on" state means (matches a checkbox's semantics), with aria-checked carrying the state. */}
        <div className='flex justify-end'>
          {/*
            Deliberately not `Switch`, and the one place in `components/task` that is.

            Twice the size, its thumb carries an icon that changes with the state, and it switches a
            VIEW rather than turning something on. Widening the shared control to cover it would
            make one control answer for two ideas, which is the failure the shared control exists to
            prevent. Exempted here rather than in the eslint config because `ignores` is per config
            OBJECT, so naming this file there would also exempt it from the raw-field bans.
          */}
          {/* eslint-disable-next-line no-restricted-syntax */}
          <button
            type='button'
            role='switch'
            aria-checked={toggleShowTable}
            aria-label={t('blocks.dataviz.table.viewToggleLabel')}
            onClick={(e) => { e.stopPropagation(); setToggleShowTable((v) => !v); }}
            onKeyDown={(e) => e.stopPropagation()}
            className='flex h-7 w-14 shrink-0 items-center rounded-full border border-ws-border bg-ws-surface p-0.5 shadow-sm'
          >
            <span
              className={`flex h-6 w-6 items-center justify-center rounded-full bg-ws-primary text-ws-btn-on-primary shadow transition-transform ${toggleShowTable ? 'translate-x-7' : 'translate-x-0'}`}
            >
              {toggleShowTable ? <TableIcon className='h-3.5 w-3.5' /> : <LineChart className='h-3.5 w-3.5' />}
            </span>
          </button>
        </div>
        {toggleShowTable ? (
          // The one mode where the table takes over the whole block — the graph (and its paired
          // sonification player) are hidden entirely while it's shown, not squeezed alongside it.
          <div className='overflow-y-auto' style={{ maxHeight: chartHeight }}>
            <DataVizTableContent data={data} />
          </div>
        ) : (
          <>
            <DataVizCanvas data={data} height={chartHeight} highlightedPoint={highlightedPoint} playheadX={playheadX} armedSeriesId={armedSeriesId} />
            {sonificationSlot}
          </>
        )}
      </div>
    );
  }

  // 'slideout' — the chart (and its paired sonification player) are always visible; the button
  // reveals the table alongside it in the same 2/3:1/3 split 'always' mode uses statically,
  // animated in via a width/height transition rather than swapped in instantly, since "slides out"
  // is the whole point of this mode.
  return (
    <div className='flex flex-col gap-1'>
      <div className='flex justify-end'>
        <button
          type='button'
          onClick={(e) => { e.stopPropagation(); setSlideoutOpen((v) => !v); }}
          onKeyDown={(e) => e.stopPropagation()}
          aria-expanded={slideoutOpen}
          className='flex items-center gap-1.5 rounded-full border border-ws-border bg-ws-surface px-3 py-1.5 text-xs font-medium text-ws-muted shadow-sm hover:bg-ws-hover hover:text-ws-text'
        >
          <TableIcon className='h-3.5 w-3.5' />
          {slideoutOpen ? t('blocks.dataviz.table.slideoutCloseButton') : t('blocks.dataviz.table.slideoutOpenButton')}
        </button>
      </div>
      <div className={isMobilePortrait ? 'flex flex-col gap-2' : 'flex gap-3'}>
        <div ref={isMobilePortrait ? undefined : setLeftColumnEl} className={`flex flex-col gap-3 transition-all ${slideoutOpen && !isMobilePortrait ? 'w-2/3 min-w-0' : 'w-full min-w-0'}`}>
          <DataVizCanvas data={data} height={chartHeight} highlightedPoint={highlightedPoint} playheadX={playheadX} armedSeriesId={armedSeriesId} />
          {sonificationSlot}
        </div>
        {slideoutOpen && (
          <div
            className={`rounded-lg border border-ws-border p-2 transition-all ${isMobilePortrait ? 'w-full' : 'w-1/3 min-w-0'}`}
            style={isMobilePortrait ? undefined : { height: sideBySideHeight }}
          >
            <div className='h-full overflow-y-auto' style={isMobilePortrait ? { maxHeight: tableMaxHeight } : undefined}>
              <DataVizTableContent data={data} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
